import { sift } from 'radash';

import { Diagram, DiagramComponentInstance } from 'models';

// Breadth-first search simplifies caching.
// We can assume that the first time a component is checked,
// it is with the highest depth value.

const _getConnectedComponentInstances = (
    diagram: Diagram,
    componentInstanceId: string,
    depth: number,
    cache: string[],
): DiagramComponentInstance[] => {
    if (cache.includes(componentInstanceId)) {
        return [];
    }

    if (depth <= 0) {
        return [getDiagramComponentInstance(diagram, componentInstanceId)];
    }

    cache.push(componentInstanceId);

    const directlyConnectedIds = sift(
        Object.values(diagram.connections).map((connection) =>
            connection.to.componentInstanceId === componentInstanceId
                ? connection.from.componentInstanceId
                : connection.from.componentInstanceId === componentInstanceId
                  ? connection.to.componentInstanceId
                  : null,
        ),
    );

    return [
        getDiagramComponentInstance(diagram, componentInstanceId),
        ...directlyConnectedIds.flatMap((id) => _getConnectedComponentInstances(diagram, id, depth - 1, cache)),
    ];
};

const getDiagramComponentInstance = (diagram: Diagram, componentInstanceId: string): DiagramComponentInstance => {
    const instance = diagram.componentInstances[componentInstanceId];

    if (!instance) {
        console.warn(`get-connected-component-instances: id ${componentInstanceId} not found in diagram`);
    }

    return instance;
};

export const getConnectedComponentInstances = (
    diagram: Diagram,
    componentInstanceId: string,
    depth = 1,
): DiagramComponentInstance[] => _getConnectedComponentInstances(diagram, componentInstanceId, depth, []);
